import Carousel from "react-native-reanimated-carousel";
import {
  Dimensions,
  View,
  StyleSheet,
  TouchableOpacity,
  ImageBackground,
  useWindowDimensions,
  Text,
} from "react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BronzeButton,
  GoldButton,
  LeftArrowPng,
  RightArrowPng,
  WinButton,
} from "../../../assets";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";

import Tournament from "../Tournaments/Tournament";
import { useDevice } from "../../../utils/hooks/useDevice";

import {
  TournamentsTrofeeBronze,
  TournamentsTrofeeSilver,
  TournamentsTrofeeGold,
} from "../../../assets";
import {
  tournamentsSubscribe,
  tournamentsUnsubscribe,
} from "../../../store/tournaments/tournamentsSagas";
import { tournamentsListRefreshRequest } from "../../../store/tournaments/tournamentsActions";
import {
  selectTournaments,
  selectTournamentsListLoading,
} from "../../../store/tournaments/tournamentsReducer";

export const TROPHIES: Record<string, any> = {
  bronze: TournamentsTrofeeBronze,
  silver: TournamentsTrofeeSilver,
  gold: TournamentsTrofeeGold,
};
export const WIN_BUTTONS: Record<string, any> = {
  bronze: BronzeButton,
  silver: WinButton,
  gold: GoldButton,
};

export type SnG = {
  id: number;
  entry_icon: "bronze" | "silver" | "gold";
  entry_level: number;
  winAmount: string;
  entry_size: string;
  users: string[];
  active_tables: number;
  players: number;
  prices: { [key: number]: number };
  registered?: boolean;
  roomId: string;
  start_in: Date | null;
  close_at: Date | null;
  tournament_chips: number;
};
export type TournamentProps = {
  sng: SnG;
  trophySource: any;
  winButtonSource: any;
  index: number;
  totalCount: number;
  isSpecial?: boolean;
  itemWidth: number;
};

export const TournamentsSection = () => {
  const auth = useSelector((state: RootState) => state.auth);

  const dispatch = useDispatch();
  // const subscribedRef = useRef(false);

  useEffect(() => {
    // if (subscribedRef.current) return;

    // subscribedRef.current = true;
    dispatch(tournamentsSubscribe);

    return () => {
      dispatch(tournamentsUnsubscribe);
      // subscribedRef.current = false;
    };
  }, [dispatch]);

  const tournaments = useSelector(selectTournaments);
  const loading = useSelector(selectTournamentsListLoading);

  useEffect(() => {
    if (!tournaments.length) return;
    const interval = setInterval(() => {
      const hasExpired = tournaments.some((s) => {
        const closeAt = (s as SnG).close_at;
        return closeAt != null && new Date(closeAt).getTime() <= Date.now();
      });
      if (hasExpired) dispatch(tournamentsListRefreshRequest());
    }, 5000);
    return () => clearInterval(interval);
  }, [tournaments, dispatch]);

  const normalizedTournaments = tournaments
    .map((sng: SnG) => ({
      ...sng,
      winAmount: Array.isArray(sng.prices)
        ? sng.prices[1]
        : (sng.prices?.["1"] ?? 0),
    }))
    // sort ascending on entry_size
    .sort((a: SnG, b: SnG) => +a.entry_size - +b.entry_size);

  const presentation = normalizedTournaments.map((sng: SnG, idx: number) => {
    const trophyKey = sng.entry_icon;
    return {
      sng,
      id: String(sng.id ?? idx),
      trophyKey,
      winButtonKey: trophyKey,
      activeTables: sng.active_tables,

      winAmount: Array.isArray(sng.prices) ? sng.prices[1] : sng.prices?.["1"],
    };
  });

  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsClient(true);
    }
  }, []);
  const carouselRef = useRef<any>(null);
  const [showArrows, setShowArrows] = useState(true);

  const { width, height } = useWindowDimensions();
  const isDesktop = width >= 980;

  const isTablet = width > 768 && width < 980;
  const { isXLg, isSm } = useDevice();

  // const isPortrait = height >= width;
  const isMobile = width < 600;

  const isSmallScreen = isMobile || isSm;

  const itemsPerPage = isSmallScreen ? 1 : 3;

  const pages = useMemo(() => {
    if (!presentation?.length) return [];

    const out: (typeof presentation)[] = [];
    for (let i = 0; i < presentation.length; i += itemsPerPage) {
      out.push(presentation.slice(i, i + itemsPerPage));
    }
    return out;
  }, [presentation, itemsPerPage]);

  const handleNext = () => {
    setShowArrows(false);
    carouselRef.current?.next();
  };

  const handlePrev = () => {
    setShowArrows(false);
    carouselRef.current?.prev();
  };

  if (!isClient) return null;

  return (
    <View
      style={{
        width,
        flexWrap: "wrap",
        flexDirection: "row",
        justifyContent: "center",
        marginTop: isDesktop ? 20 : 20,
      }}
    >
      {loading ? (
        <View style={{ alignItems: "center", justifyContent: "center" }}>
          <Text style={{ color: "#B7C1D1", marginTop: 8 }}>
            Loading tournaments…
          </Text>
        </View>
      ) : presentation.length === 0 ? (
        <Text style={{ color: "#fff" }}>No tournaments available</Text>
      ) : isSmallScreen ? (
        <>
          <Carousel
            ref={carouselRef}
            loop={false}
            width={width}
            height={
              isSmallScreen || isTablet
                ? height * 0.7
                : isXLg
                  ? height * 0.5
                  : height * 0.9
            }
            autoPlay={false}
            data={pages}
            pagingEnabled
            scrollAnimationDuration={1000}
            onScrollEnd={() => setShowArrows(true)}
            renderItem={({ item: page, index: pageIndex }) => (
              <View
                key={`page-${pageIndex}`}
                style={{
                  flexDirection: "row",
                  justifyContent: isTablet ? "space-between" : "center",

                  width: isSmallScreen
                    ? width
                    : isTablet
                      ? width - 120
                      : width - 300,
                  height: "100%",
                  paddingHorizontal: 8,
                  margin: "auto" as any,
                  alignItems: "center",
                  alignSelf: "center",
                }}
              >
                {page.map((t, itemIndex) => {
                  const isSpecial =
                    (isDesktop || isTablet) &&
                    itemIndex === 1 &&
                    page.length === 3;
                  const globalIndex = pageIndex * itemsPerPage + itemIndex;

                  return (
                    <View
                      key={t.id ?? `${pageIndex}-${itemIndex}`}
                      style={{
                        width: isSmallScreen
                          ? width
                          : isTablet
                            ? width / 3 - 70
                            : width / 3 - 120,
                        marginHorizontal: 1,
                        alignItems: "center",
                      }}
                    >
                      <Tournament
                        sng={t.sng}
                        trophySource={TROPHIES[t.trophyKey]}
                        winButtonSource={
                          WIN_BUTTONS[t.winButtonKey] ?? WIN_BUTTONS.gold
                        }
                        index={globalIndex}
                        totalCount={presentation.length}
                        isSpecial={isSpecial}
                        itemWidth={
                          isSmallScreen
                            ? width - 32
                            : isTablet
                              ? width / 3 - 70
                              : width / 3 - 120
                        }
                      />
                    </View>
                  );
                })}
              </View>
            )}
          />

          {/* arrows only when carousel is used */}
          <TouchableOpacity style={styles.arrowLeft} onPress={handlePrev}>
            <ImageBackground
              source={LeftArrowPng}
              resizeMode="cover"
              style={styles.arrowBg}
              imageStyle={styles.arrowImg}
            />
          </TouchableOpacity>

          <TouchableOpacity style={styles.arrowRight} onPress={handleNext}>
            <ImageBackground
              source={RightArrowPng}
              resizeMode="cover"
              style={styles.arrowBg}
              imageStyle={styles.arrowImg}
            />
          </TouchableOpacity>
        </>
      ) : (
        <View
          style={{
            width,
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingHorizontal: 8,
          }}
        >
          {presentation.map((t, index) => {
            const isSpecial =
              (isDesktop || isTablet) &&
              presentation.length >= 3 &&
              index % 3 === 1;

            return (
              <View
                key={t.id}
                style={{
                  width: isTablet ? width / 3 - 70 : width / 3 - 90,
                  marginHorizontal: 4,
                  marginVertical: 10,
                }}
              >
                <Tournament
                  sng={t.sng}
                  trophySource={TROPHIES[t.trophyKey]}
                  winButtonSource={
                    WIN_BUTTONS[t.winButtonKey] ?? WIN_BUTTONS.gold
                  }
                  index={index}
                  totalCount={presentation.length}
                  isSpecial={isSpecial}
                  itemWidth={
                    isSmallScreen
                      ? width - 32
                      : isTablet
                        ? width / 3 - 10
                        : width / 3 - 120
                  }
                />
              </View>
            );
          })}
        </View>
      )}
    </View>
  );

  // <View
  //   style={[
  //     styles.container,
  //     {
  //       width: width,
  //       marginTop: isDesktop ? 20 : 50,
  //       height: "100%",
  //       //  height: isDesktop ? height * 0.8 : height * 0.7,
  //     },
  //   ]}
  // >
  //   <Carousel
  //     ref={carouselRef}
  //     loop={false}
  //     width={width}
  //     // height={isDesktop ? 320 : 480}
  //     height={
  //       isMobile || isTablet || isSm
  //         ? height * 0.7
  //         : isXLg
  //           ? height * 0.5
  //           : height * 0.9
  //     }
  //     autoPlay={false}
  //     data={pages}
  //     pagingEnabled
  //     scrollAnimationDuration={1000}
  //     onScrollEnd={() => setShowArrows(true)}
  //     renderItem={({ item: page, index: pageIndex }) => (
  //       <View
  //         key={`page-${pageIndex}`}
  //         style={{
  //           flexDirection: "row",
  //           justifyContent: isTablet ? "space-between" : "center",
  //           width: isMobile ? width : isTablet ? width - 120 : width - 300,
  //           // height: "100%",
  //           paddingHorizontal: 8,
  //           margin: "auto" as any,
  //           alignItems: "center",
  //         }}
  //       >
  //         {page.map((t, itemIndex) => {
  //           const isSpecial =
  //             (isDesktop || isTablet) && itemIndex === 1 && page.length === 3;
  //           const globalIndex = pageIndex * itemsPerPage + itemIndex;
  //           return (
  //             <View
  //               key={t.id ?? `${pageIndex}-${itemIndex}`}
  //               style={{
  //                 width: isMobile
  //                   ? width
  //                   : isTablet
  //                     ? width / 3 - 70
  //                     : width / 3 - 120,
  //                 marginHorizontal: 1,
  //                 // height: "100%",
  //               }}
  //             >
  //               <Tournament
  //                 sng={t.sng}
  //                 trophySource={TROPHIES[t.trophyKey]}
  //                 winButtonSource={
  //                   WIN_BUTTONS[t.winButtonKey] ?? WIN_BUTTONS.gold
  //                 }
  //                 index={globalIndex}
  //                 totalCount={presentation.length}
  //                 isSpecial={isSpecial}
  //               />
  //             </View>
  //           );
  //         })}
  //       </View>
  //     )}
  //   />

  //   {/* <>
  //     <TouchableOpacity style={styles.arrowLeft} onPress={handlePrev}>
  //       <ImageBackground
  //         source={LeftArrowPng}
  //         resizeMode="cover"
  //         style={styles.arrowBg}
  //         imageStyle={styles.arrowImg}
  //       />
  //     </TouchableOpacity>

  //     <TouchableOpacity style={styles.arrowRight} onPress={handleNext}>
  //       <ImageBackground
  //         source={RightArrowPng}
  //         resizeMode="cover"
  //         style={styles.arrowBg}
  //         imageStyle={styles.arrowImg}
  //       />
  //     </TouchableOpacity>
  //   </> */}
  //   {/* )} */}
  // </View>
  // );
};

export default TournamentsSection;

const styles = StyleSheet.create({
  container: {
    position: "relative",

    // height: 450,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
  },
  arrowLeft: {
    position: "absolute",
    top: "45%",
    left: 10,
    borderRadius: 20,
    zIndex: 10,
  },
  arrowRight: {
    position: "absolute",
    top: "45%",
    right: 10,
    borderRadius: 20,
    zIndex: 10,
  },
  arrowBg: {
    width: 50,
    height: 50,
    justifyContent: "center",
    alignItems: "center",
    padding: 10,
    borderRadius: 20,
  },
  arrowImg: {
    borderRadius: 20,
  },
});
